// src/pages/Social.js
// Page principale Fynho Social - design premium, carte contenue, icônes modernes
// Recherche utilisateur toujours visible en haut, invitations envoyées annulables

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Map, PartyPopper, Users, Mail, Search, MessageCircle,
  UserMinus, ShieldOff, UserPlus, Check, X, Sparkles, Send,
} from 'lucide-react';
import { useTheme } from '../hooks/useTheme';
import { useAuth } from '../hooks/useAuth';
import ROUTES from '../constants/routes';
import * as socialApi from '../utils/socialApi';
import CreateProfileModal from '../components/social/CreateProfileModal';
import SocialMap from '../components/social/SocialMap';
import EventCard from '../components/social/EventCard';
import ChatWindow from '../components/social/ChatWindow';

const DEFAULT_CITY = 'Douala';

function extractUserId(user) {
  if (!user) return null;
  return user.id ?? user.user_id ?? user._id ?? user.userId ?? null;
}

const Social = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated, isAdminAuthenticated, user } = useAuth();
  const isAdmin = isAdminAuthenticated;
  const socialUserId = extractUserId(user);

  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileFetchFailed, setProfileFetchFailed] = useState(false);
  const [showCreateProfile, setShowCreateProfile] = useState(false);
  const [activeTab, setActiveTab] = useState('map');
  const [invitationsSubTab, setInvitationsSubTab] = useState('received');
  const [pendingCount, setPendingCount] = useState(0);
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [chatTarget, setChatTarget] = useState(null);
  const [sentInvites, setSentInvites] = useState(new Set());

  const pollRef = useRef(null);
  const searchWrapperRef = useRef(null);

  const canInteract = isAuthenticated && !!profile;

  const requireLogin = useCallback(() => {
    navigate(ROUTES.LOGIN);
  }, [navigate]);

  const requireProfile = useCallback(() => {
    setShowCreateProfile(true);
  }, []);

  const requireAction = useCallback(() => {
    if (!isAuthenticated) requireLogin();
    else requireProfile();
  }, [isAuthenticated, requireLogin, requireProfile]);

  const loadProfile = useCallback(async () => {
    if (!isAuthenticated || !socialUserId) {
      setProfile(null);
      setProfileFetchFailed(false);
      setProfileLoading(false);
      return;
    }
    setProfileLoading(true);
    try {
      const res = await socialApi.getProfile(socialUserId);
      if (res && Object.prototype.hasOwnProperty.call(res, 'profile')) {
        setProfile(res.profile || null);
        setProfileFetchFailed(false);
        if (res.profile) {
          socialApi.updatePresence(socialUserId, true).catch(() => {});
        }
      } else {
        setProfileFetchFailed(true);
      }
    } catch (err) {
      setProfileFetchFailed(true);
    } finally {
      setProfileLoading(false);
    }
  }, [isAuthenticated, socialUserId]);

  useEffect(() => {
    loadProfile();

    const handleUnload = () => {
      if (socialUserId) socialApi.updatePresence(socialUserId, false).catch(() => {});
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      handleUnload();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, socialUserId]);

  useEffect(() => {
    if (!profile) return;
    const poll = async () => {
      try {
        const res = await socialApi.listFriendRequests(socialUserId);
        setPendingCount(res.count || 0);
      } catch (err) {
        // silencieux
      }
    };
    poll();
    pollRef.current = setInterval(poll, 15000);
    return () => clearInterval(pollRef.current);
  }, [profile, socialUserId]);

  const loadFriends = useCallback(async () => {
    const res = await socialApi.listFriends(socialUserId);
    setFriends(res.friends || []);
  }, [socialUserId]);

  const loadRequests = useCallback(async () => {
    const res = await socialApi.listFriendRequests(socialUserId);
    setRequests(res.requests || []);
    setPendingCount(res.count || 0);
  }, [socialUserId]);

  const loadSentRequests = useCallback(async () => {
    const res = await socialApi.listSentRequests(socialUserId);
    const list = res.requests || [];
    setSentRequests(list);
    setSentInvites(new Set(list.map((r) => r.user_id)));
  }, [socialUserId]);

  useEffect(() => {
    if (!profile) return;
    if (activeTab === 'friends') loadFriends();
    if (activeTab === 'invitations' && invitationsSubTab === 'received') loadRequests();
    if (activeTab === 'invitations' && invitationsSubTab === 'sent') loadSentRequests();
  }, [activeTab, invitationsSubTab, profile, loadFriends, loadRequests, loadSentRequests]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target)) {
        setSearchResults([]);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAcceptRequest = async (requestId) => {
    await socialApi.respondFriendRequest(requestId, 'accepted');
    loadRequests();
    loadFriends();
  };

  const handleRefuseRequest = async (requestId) => {
    await socialApi.respondFriendRequest(requestId, 'refused');
    loadRequests();
  };

  const handleRemoveFriend = async (friendId) => {
    await socialApi.removeFriend(socialUserId, friendId);
    loadFriends();
  };

  const handleBlockFriend = async (blockedId) => {
    await socialApi.blockUser(socialUserId, blockedId);
    loadFriends();
  };

  const handleSearch = async (value) => {
    setSearchQuery(value);
    if (value.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    const res = await socialApi.searchUser(value);
    const filtered = (res.results || []).filter((u) => String(u.user_id) !== String(socialUserId));
    setSearchResults(filtered);
  };

  const handleSendInvite = async (receiverId) => {
    if (!canInteract) return requireAction();
    try {
      await socialApi.sendFriendRequest(socialUserId, receiverId);
      setSentInvites((prev) => new Set(prev).add(receiverId));
    } catch (err) {
      // silencieux, l'utilisateur peut réessayer
    }
  };

  const handleCancelInviteFromList = async (receiverId) => {
    try {
      await socialApi.cancelFriendRequest(socialUserId, receiverId);
      setSentInvites((prev) => {
        const next = new Set(prev);
        next.delete(receiverId);
        return next;
      });
      setSentRequests((prev) => prev.filter((r) => r.user_id !== receiverId));
    } catch (err) {
      // silencieux
    }
  };

  const handleMapInviteSent = (targetUserId) => {
    setSentInvites((prev) => new Set(prev).add(targetUserId));
  };

  const handleMapInviteCancelled = (targetUserId) => {
    setSentInvites((prev) => {
      const next = new Set(prev);
      next.delete(targetUserId);
      return next;
    });
  };

  const handleOpenChat = (friendId, username) => {
    if (!canInteract) return requireAction();
    setChatTarget({ userId: friendId, username });
  };

  const styles = {
    page: { display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: theme.bg, paddingBottom: '84px' },
    hero: {
      position: 'relative', padding: '30px 20px 18px',
      background: `linear-gradient(150deg, ${theme.goldSoft} 0%, transparent 65%)`,
      overflow: 'hidden',
    },
    heroGlow: {
      position: 'absolute', top: '-60px', right: '-40px', width: '180px', height: '180px',
      borderRadius: '50%', background: `radial-gradient(circle, ${theme.gold}22, transparent 70%)`,
      pointerEvents: 'none',
    },
    heroTop: { display: 'flex', alignItems: 'center', gap: '8px' },
    heroTitle: { fontSize: '25px', fontWeight: '800', color: theme.textPrimary, letterSpacing: '-0.6px' },
    heroSubtitle: { fontSize: '13px', color: theme.textSecondary, marginTop: '7px', lineHeight: '1.55', maxWidth: '420px' },
    heroUserPill: {
      display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '14px',
      padding: '7px 14px', backgroundColor: theme.bgCard, border: `1px solid ${theme.goldBorder}`,
      borderRadius: '20px', fontSize: '12.5px', fontWeight: '700', color: theme.gold,
    },
    topSearchWrap: { position: 'relative', padding: '0 16px 14px' },
    topSearchInputWrap: { position: 'relative' },
    topSearchIcon: { position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: theme.textMuted, pointerEvents: 'none' },
    topSearchInput: {
      width: '100%', padding: '12px 14px 12px 40px', backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`,
      borderRadius: '14px', color: theme.textPrimary, fontSize: '13.5px', outline: 'none', boxSizing: 'border-box',
      transition: 'border-color 150ms ease',
    },
    topSearchDropdown: {
      position: 'absolute', top: 'calc(100% - 4px)', left: '16px', right: '16px', zIndex: 200,
      backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: '14px',
      boxShadow: theme.mode === 'light' ? '0 10px 30px rgba(0,0,0,0.12)' : '0 10px 30px rgba(0,0,0,0.5)',
      overflow: 'hidden', maxHeight: '320px', overflowY: 'auto',
    },
    topSearchResultRow: {
      display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 14px',
      borderBottom: `1px solid ${theme.borderSoft}`,
    },
    topSearchEmpty: { padding: '16px', fontSize: '12.5px', color: theme.textMuted, textAlign: 'center' },
    banner: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
      margin: '0 16px 14px', padding: '13px 16px', backgroundColor: theme.bgCard,
      border: `1px solid ${theme.goldBorder}`, borderRadius: '14px',
    },
    bannerText: { fontSize: '12.5px', color: theme.textSecondary, lineHeight: '1.4' },
    bannerBtn: {
      padding: '9px 16px', backgroundColor: theme.gold, border: 'none', borderRadius: '10px',
      color: '#FFFFFF', fontSize: '12.5px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap',
      flexShrink: 0,
    },
    tabsRow: {
      display: 'flex', gap: '8px', padding: '4px 16px 16px', overflowX: 'auto', scrollbarWidth: 'none',
    },
    tab: {
      position: 'relative', display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px',
      borderRadius: '12px', backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`,
      color: theme.textSecondary, fontSize: '12.5px', fontWeight: '700', cursor: 'pointer',
      whiteSpace: 'nowrap', flexShrink: 0, transition: 'all 180ms ease',
    },
    tabActive: {
      backgroundColor: theme.gold, color: '#FFFFFF', border: `1px solid ${theme.gold}`,
      boxShadow: `0 4px 12px ${theme.gold}40`,
    },
    badge: {
      position: 'absolute', top: '-5px', right: '-5px', backgroundColor: '#F56565', color: '#FFFFFF',
      fontSize: '10px', fontWeight: '800', borderRadius: '50%', width: '17px', height: '17px',
      display: 'flex', alignItems: 'center', justifyContent: 'center', border: `2px solid ${theme.bg}`,
    },
    body: { flex: 1, padding: '0 16px 16px' },
    subTabsRow: { display: 'flex', gap: '8px', marginBottom: '14px' },
    subTab: {
      flex: 1, textAlign: 'center', padding: '9px 12px', borderRadius: '10px',
      backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`, color: theme.textSecondary,
      fontSize: '12.5px', fontWeight: '700', cursor: 'pointer', transition: 'all 180ms ease',
    },
    subTabActive: { backgroundColor: theme.gold, color: '#FFFFFF', border: `1px solid ${theme.gold}` },
    friendRow: {
      display: 'flex', alignItems: 'center', gap: '12px', padding: '13px', backgroundColor: theme.bgCard,
      borderRadius: '14px', border: `1px solid ${theme.border}`, marginBottom: '10px',
      transition: 'transform 150ms ease',
    },
    avatarWrap: { position: 'relative', flexShrink: 0 },
    avatar: {
      width: '46px', height: '46px', borderRadius: '50%', backgroundColor: theme.bgInput,
      objectFit: 'cover', border: `2px solid ${theme.border}`,
    },
    avatarSmall: {
      width: '38px', height: '38px', borderRadius: '50%', backgroundColor: theme.bgInput,
      objectFit: 'cover', border: `2px solid ${theme.border}`, flexShrink: 0,
    },
    onlineDot: {
      position: 'absolute', bottom: '1px', right: '1px', width: '11px', height: '11px', borderRadius: '50%',
      backgroundColor: '#48BB78', border: `2px solid ${theme.bgCard}`,
    },
    friendInfo: { flex: 1, minWidth: 0 },
    friendName: { fontSize: '13.5px', fontWeight: '700', color: theme.textPrimary, display: 'flex', alignItems: 'center', gap: '6px' },
    friendNameSmall: { fontSize: '13px', fontWeight: '700', color: theme.textPrimary },
    friendLoc: { fontSize: '11.5px', color: theme.textSecondary, marginTop: '1px' },
    friendLocSmall: { fontSize: '11px', color: theme.textSecondary },
    actionsRow: { display: 'flex', gap: '6px', flexShrink: 0 },
    iconBtn: {
      display: 'flex', alignItems: 'center', justifyContent: 'center', width: '34px', height: '34px',
      borderRadius: '10px', border: 'none', cursor: 'pointer', transition: 'transform 120ms ease',
    },
    inviteBtn: {
      display: 'flex', alignItems: 'center', gap: '5px', padding: '8px 13px', borderRadius: '10px',
      border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '700', backgroundColor: theme.gold, color: '#FFFFFF',
      flexShrink: 0,
    },
    invitedBadge: {
      display: 'flex', alignItems: 'center', gap: '5px', padding: '8px 13px', borderRadius: '10px',
      fontSize: '12px', fontWeight: '700', backgroundColor: `${theme.gold}18`, color: theme.gold,
      border: `1px solid ${theme.goldBorder}`, flexShrink: 0,
    },
    cancelInviteBtn: {
      display: 'flex', alignItems: 'center', gap: '5px', padding: '8px 13px', borderRadius: '10px',
      border: '1px solid #F56565', cursor: 'pointer', fontSize: '12px', fontWeight: '700',
      backgroundColor: 'transparent', color: '#F56565', flexShrink: 0,
    },
    emptyState: {
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', textAlign: 'center',
      padding: '40px 24px', color: theme.textSecondary, fontSize: '13px', lineHeight: '1.5',
    },
    prompt: { textAlign: 'center', padding: '52px 24px', display: 'flex', flexDirection: 'column', gap: '14px', alignItems: 'center' },
    promptIconCircle: {
      width: '56px', height: '56px', borderRadius: '50%', backgroundColor: theme.goldSoft,
      display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.gold,
    },
    promptText: { fontSize: '14px', color: theme.textSecondary, maxWidth: '260px', lineHeight: '1.5' },
    promptBtn: {
      padding: '13px 26px', backgroundColor: theme.gold, border: 'none', borderRadius: '12px',
      color: '#FFFFFF', fontSize: '14px', fontWeight: '700', cursor: 'pointer',
    },
  };

  const tabs = [
    { id: 'map', icon: Map, label: 'Carte' },
    { id: 'events', icon: PartyPopper, label: 'Événements' },
    { id: 'friends', icon: Users, label: 'Amis' },
    { id: 'invitations', icon: Mail, label: 'Invitations', badge: pendingCount },
  ];

  if (profileLoading) {
    return <div style={{ ...styles.page, alignItems: 'center', justifyContent: 'center' }}>...</div>;
  }

  const cityForBrowsing = profile ? profile.city : DEFAULT_CITY;
  const showDropdown = searchQuery.trim().length >= 2;

  return (
    <div style={styles.page}>
      <Helmet><title>Fynho Social{profile ? ` | @${profile.username}` : ''}</title></Helmet>

      <div style={styles.hero}>
        <div style={styles.heroGlow} />
        <div style={styles.heroTop}>
          <Sparkles size={20} color={theme.gold} />
          <div style={styles.heroTitle}>Fynho Social</div>
        </div>
        <div style={styles.heroSubtitle}>
          Découvre les personnes et événements autour de toi.
        </div>
        {profile && (
          <div style={styles.heroUserPill}>@{profile.username} · {profile.district}, {profile.city}</div>
        )}
      </div>

      <div style={styles.topSearchWrap} ref={searchWrapperRef}>
        <div style={styles.topSearchInputWrap}>
          <span style={styles.topSearchIcon}><Search size={16} /></span>
          <input
            style={styles.topSearchInput}
            type="text"
            placeholder="Rechercher un @username..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>

        {showDropdown && (
          <div style={styles.topSearchDropdown}>
            {searchResults.length === 0 ? (
              <div style={styles.topSearchEmpty}>Aucun résultat pour "{searchQuery}"</div>
            ) : (
              searchResults.map((resultUser) => (
                <div key={resultUser.user_id} style={styles.topSearchResultRow}>
                  <div style={styles.avatarWrap}>
                    <img src={resultUser.avatar || '/images/avatar-placeholder.png'} alt={resultUser.username} style={styles.avatarSmall} />
                    {resultUser.is_online && <span style={styles.onlineDot} />}
                  </div>
                  <div style={styles.friendInfo}>
                    <div style={styles.friendNameSmall}>{resultUser.username}</div>
                    <div style={styles.friendLocSmall}>{resultUser.district}, {resultUser.city}</div>
                  </div>
                  {sentInvites.has(resultUser.user_id) ? (
                    <button style={styles.cancelInviteBtn} onClick={() => handleCancelInviteFromList(resultUser.user_id)}>
                      <X size={14} /> Annuler
                    </button>
                  ) : (
                    <button style={styles.inviteBtn} onClick={() => handleSendInvite(resultUser.user_id)}>
                      <UserPlus size={14} /> Inviter
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {!isAuthenticated && (
        <div style={styles.banner}>
          <span style={styles.bannerText}>Connecte-toi pour inviter, discuter et créer des événements.</span>
          <button style={styles.bannerBtn} onClick={requireLogin}>Se connecter</button>
        </div>
      )}

      {isAuthenticated && profileFetchFailed && (
        <div style={styles.banner}>
          <span style={styles.bannerText}>
            Impossible de vérifier ton profil pour le moment (problème de connexion).
          </span>
          <button style={styles.bannerBtn} onClick={loadProfile}>Réessayer</button>
        </div>
      )}

      {isAuthenticated && !profileFetchFailed && !profile && (
        <div style={styles.banner}>
          <span style={styles.bannerText}>
            Complète ton profil (pseudo, quartier) pour apparaître sur la carte et interagir.
          </span>
          <button style={styles.bannerBtn} onClick={requireProfile}>Compléter mon profil</button>
        </div>
      )}

      <div style={styles.tabsRow} className="hide-scrollbar">
        <style>{`.hide-scrollbar::-webkit-scrollbar { display: none; }`}</style>
        {tabs.map((tabItem) => {
          const TabIcon = tabItem.icon;
          const isActive = activeTab === tabItem.id;
          return (
            <div
              key={tabItem.id}
              style={{ ...styles.tab, ...(isActive ? styles.tabActive : {}) }}
              onClick={() => setActiveTab(tabItem.id)}
            >
              <TabIcon size={15} strokeWidth={2.3} />
              {tabItem.label}
              {tabItem.badge > 0 && <span style={styles.badge}>{tabItem.badge}</span>}
            </div>
          );
        })}
      </div>

      <div style={styles.body}>
        {activeTab === 'map' && (
          <SocialMap
            city={cityForBrowsing}
            userId={socialUserId}
            isAuthenticated={canInteract}
            onRequireLogin={requireAction}
            friendIds={new Set(friends.map((f) => f.user_id))}
            sentRequestIds={sentInvites}
            onOpenChat={handleOpenChat}
            onInviteSent={handleMapInviteSent}
            onInviteCancelled={handleMapInviteCancelled}
          />
        )}

        {activeTab === 'events' && (
          <EventCard
            userId={socialUserId}
            city={cityForBrowsing}
            isAdmin={isAdmin}
            isAuthenticated={canInteract}
            onRequireLogin={requireAction}
          />
        )}

        {activeTab === 'friends' && (
          !isAuthenticated ? (
            <div style={styles.prompt}>
              <div style={styles.promptIconCircle}><Users size={26} /></div>
              <div style={styles.promptText}>Connecte-toi pour voir et gérer tes amis.</div>
              <button style={styles.promptBtn} onClick={requireLogin}>Se connecter</button>
            </div>
          ) : !profile ? (
            <div style={styles.prompt}>
              <div style={styles.promptIconCircle}><Sparkles size={26} /></div>
              <div style={styles.promptText}>Complète ton profil social pour voir tes amis.</div>
              <button style={styles.promptBtn} onClick={requireProfile}>Compléter mon profil</button>
            </div>
          ) : (
            <>
              {friends.length === 0 && (
                <div style={styles.emptyState}>
                  <Users size={32} color={theme.textMuted} />
                  Aucun ami pour le moment. Utilise la recherche en haut pour en trouver !
                </div>
              )}
              {friends.map((friend) => (
                <div key={friend.user_id} style={styles.friendRow}>
                  <div style={styles.avatarWrap}>
                    <img src={friend.avatar || '/images/avatar-placeholder.png'} alt={friend.username} style={styles.avatar} />
                    {friend.is_online && <span style={styles.onlineDot} />}
                  </div>
                  <div style={styles.friendInfo}>
                    <div style={styles.friendName}>{friend.username}</div>
                    <div style={styles.friendLoc}>{friend.district}, {friend.city}</div>
                  </div>
                  <div style={styles.actionsRow}>
                    <button
                      style={{ ...styles.iconBtn, backgroundColor: `${theme.gold}22`, color: theme.gold }}
                      onClick={() => handleOpenChat(friend.user_id, friend.username)}
                      title="Message"
                    >
                      <MessageCircle size={16} />
                    </button>
                    <button
                      style={{ ...styles.iconBtn, backgroundColor: theme.bgInput, color: theme.textSecondary }}
                      onClick={() => handleRemoveFriend(friend.user_id)}
                      title="Retirer"
                    >
                      <UserMinus size={16} />
                    </button>
                    <button
                      style={{ ...styles.iconBtn, backgroundColor: 'rgba(245, 101, 101, 0.12)', color: '#F56565' }}
                      onClick={() => handleBlockFriend(friend.user_id)}
                      title="Bloquer"
                    >
                      <ShieldOff size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </>
          )
        )}

        {activeTab === 'invitations' && (
          !isAuthenticated ? (
            <div style={styles.prompt}>
              <div style={styles.promptIconCircle}><Mail size={26} /></div>
              <div style={styles.promptText}>Connecte-toi pour voir tes invitations.</div>
              <button style={styles.promptBtn} onClick={requireLogin}>Se connecter</button>
            </div>
          ) : !profile ? (
            <div style={styles.prompt}>
              <div style={styles.promptIconCircle}><Sparkles size={26} /></div>
              <div style={styles.promptText}>Complète ton profil social pour voir tes invitations.</div>
              <button style={styles.promptBtn} onClick={requireProfile}>Compléter mon profil</button>
            </div>
          ) : (
            <>
              <div style={styles.subTabsRow}>
                <div
                  style={{ ...styles.subTab, ...(invitationsSubTab === 'received' ? styles.subTabActive : {}) }}
                  onClick={() => setInvitationsSubTab('received')}
                >
                  Reçues {pendingCount > 0 && `(${pendingCount})`}
                </div>
                <div
                  style={{ ...styles.subTab, ...(invitationsSubTab === 'sent' ? styles.subTabActive : {}) }}
                  onClick={() => setInvitationsSubTab('sent')}
                >
                  Envoyées
                </div>
              </div>

              {invitationsSubTab === 'received' && (
                <>
                  {requests.length === 0 && (
                    <div style={styles.emptyState}>
                      <Mail size={32} color={theme.textMuted} />
                      Aucune invitation en attente.
                    </div>
                  )}
                  {requests.map((req) => (
                    <div key={req.id} style={styles.friendRow}>
                      <div style={styles.avatarWrap}>
                        <img src={req.avatar || '/images/avatar-placeholder.png'} alt={req.username} style={styles.avatar} />
                      </div>
                      <div style={styles.friendInfo}>
                        <div style={styles.friendName}>{req.username}</div>
                        <div style={styles.friendLoc}>{req.district}, {req.city}</div>
                      </div>
                      <div style={styles.actionsRow}>
                        <button
                          style={{ ...styles.iconBtn, backgroundColor: `${theme.gold}22`, color: theme.gold }}
                          onClick={() => handleAcceptRequest(req.id)}
                          title="Accepter"
                        >
                          <Check size={17} />
                        </button>
                        <button
                          style={{ ...styles.iconBtn, backgroundColor: 'rgba(245, 101, 101, 0.12)', color: '#F56565' }}
                          onClick={() => handleRefuseRequest(req.id)}
                          title="Refuser"
                        >
                          <X size={17} />
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {invitationsSubTab === 'sent' && (
                <>
                  {sentRequests.length === 0 && (
                    <div style={styles.emptyState}>
                      <Send size={32} color={theme.textMuted} />
                      Aucune invitation envoyée en attente.
                    </div>
                  )}
                  {sentRequests.map((req) => (
                    <div key={req.id} style={styles.friendRow}>
                      <div style={styles.avatarWrap}>
                        <img src={req.avatar || '/images/avatar-placeholder.png'} alt={req.username} style={styles.avatar} />
                      </div>
                      <div style={styles.friendInfo}>
                        <div style={styles.friendName}>{req.username}</div>
                        <div style={styles.friendLoc}>{req.district}, {req.city}</div>
                      </div>
                      <button style={styles.cancelInviteBtn} onClick={() => handleCancelInviteFromList(req.user_id)}>
                        <X size={14} /> Annuler
                      </button>
                    </div>
                  ))}
                </>
              )}
            </>
          )
        )}
      </div>

      {showCreateProfile && (
        <CreateProfileModal
          userId={socialUserId}
          onClose={() => setShowCreateProfile(false)}
          onCreated={(newProfile) => {
            setProfile(newProfile);
            setShowCreateProfile(false);
          }}
        />
      )}

      {chatTarget && (
        <ChatWindow
          userId={socialUserId}
          friendId={chatTarget.userId}
          friendUsername={chatTarget.username}
          onClose={() => setChatTarget(null)}
        />
      )}
    </div>
  );
};

export default Social;